import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import type { ModuleManifest, ModuleId } from './types';
import { ecommerceModule } from './ecommerce';
import { elearningModule } from './elearning';
import { socialModule } from './social';
import { blogModule } from './blog';
import { contactModule } from './contact';
import { bookingModule } from './booking';
import { adminModule } from './admin';
import { paymentsModule } from './payments';
import { customerAuthModule } from './customer-auth';
import { crmModule } from './crm';
import { emailModule } from './email';
import { reviewsModule } from './reviews';

export interface RegisteredRoute {
  path: string;
  moduleId: ModuleId;
  Component: LazyExoticComponent<ComponentType>;
}

/** PURE composition — unit-testable with injected manifests (no flag globals).
 *  lazy() is applied HERE and nowhere else (see types.ts contract). */
export const composeRoutes = (modules: ModuleManifest[]): RegisteredRoute[] =>
  modules.flatMap((m) =>
    m.routes.map((r) => ({ path: r.path, moduleId: m.id, Component: lazy(r.load) }))
  );

// ---- Flag-guarded assembly ----
// CRITICAL: bare `__MODULE_X__` identifiers in plain if-statements. Do NOT alias,
// destructure, loop over, or window-prefix them — Vite define replaces the bare
// identifier with a literal; `if (false)` branches are then eliminated by Rollup,
// which drops the unused manifest import AND every chunk its loaders reference.
const installed: ModuleManifest[] = [];
if (__MODULE_ECOMMERCE__) installed.push(ecommerceModule);
if (__MODULE_ELEARNING__) installed.push(elearningModule);
if (__MODULE_SOCIAL__) installed.push(socialModule);
if (__MODULE_BLOG__) installed.push(blogModule);
if (__MODULE_CONTACT__) installed.push(contactModule);
if (__MODULE_BOOKING__) installed.push(bookingModule);
if (__MODULE_ADMIN__) installed.push(adminModule);
if (__MODULE_PAYMENTS__) installed.push(paymentsModule);
if (__MODULE_CUSTOMER_AUTH__) installed.push(customerAuthModule);
if (__MODULE_CRM__) installed.push(crmModule);
if (__MODULE_EMAIL__) installed.push(emailModule);
if (__MODULE_REVIEWS__) installed.push(reviewsModule);

/** Tier-1 "installed" modules for this build (tier-2 runtime state: PRP-3/6). */
export const enabledModules: ModuleManifest[] = installed;

/** Flattened adminPanel entries from enabled manifests, preserving module order.
 *  Pure data + arrows; lazy() is applied by AdminApp (PRP-2 side-effect rule). */
export function getAdminPanels() {
  return installed.flatMap((m) =>
    m.adminPanel ? (Array.isArray(m.adminPanel) ? m.adminPanel : [m.adminPanel]) : []
  );
}
export const enabledModuleIds: ModuleId[] = installed.map((m) => m.id);
export const moduleRoutes: RegisteredRoute[] = composeRoutes(installed);
